import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Usage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Usage() {
  const { user } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usageData, setUsageData] = useState([]); // Store raw usage data for tooltips
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    // Ensure start date is not before 2025-06-05
    const minDate = new Date('2025-06-05');
    const startDate = thirtyDaysAgo < minDate ? minDate : thirtyDaysAgo;
    
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    });
  }, []);

  const getChartColors = () => {
    const isDarkMode = document.documentElement.classList.contains('dark-mode');
    
    if (isDarkMode) {
      return {
        successful: {
          border: 'rgb(76, 201, 240)', // primary-color in dark mode
          background: 'rgba(76, 201, 240, 0.1)',
        },
        failed: {
          border: 'rgb(248, 113, 113)', // error-color in dark mode
          background: 'rgba(248, 113, 113, 0.1)',
        },
      };
    } else {
      return {
        successful: {
          border: 'rgb(59, 130, 246)', // blue-500
          background: 'rgba(59, 130, 246, 0.1)',
        },
        failed: {
          border: 'rgb(239, 68, 68)', // red-500
          background: 'rgba(239, 68, 68, 0.1)',
        },
      };
    }
  };

  const fetchUsageData = async () => {
    if (!user || !dateRange.startDate || !dateRange.endDate) {
      console.log('Skipping fetch - missing data:', { user: !!user, startDate: dateRange.startDate, endDate: dateRange.endDate });
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching usage data for user:', user.id, 'with dates:', dateRange);
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/usage-chart-data`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Raw API response:', response.data);
      
      const data = response.data.data;
      
      console.log('Processed data from API:', data);
      
      // Generate all dates in the range
      const allDates = [];
      const currentDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      while (currentDate <= endDate) {
        allDates.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      console.log('All dates in range:', allDates);
      
      // Create data map for easy lookup
      const dataMap = {};
      data.forEach(item => {
        dataMap[item.date] = item;
      });
      
      console.log('Data map:', dataMap);
      
      // Fill in missing dates with 0 values and store complete usage data
      const successfulData = [];
      const failedData = [];
      const labels = [];
      const completeUsageData = [];
      
      allDates.forEach(date => {
        const item = dataMap[date];
        successfulData.push(item ? parseInt(item.successful_lookups) : 0);
        failedData.push(item ? parseInt(item.failed_lookups) : 0);
        labels.push(new Date(date).toLocaleDateString());
        completeUsageData.push({
          date,
          successful_lookups: item ? parseInt(item.successful_lookups) : 0,
          failed_lookups: item ? parseInt(item.failed_lookups) : 0,
          failure_reasons: item ? item.failure_reasons : {}
        });
      });
      
      console.log('Chart data arrays:', { successfulData, failedData, labels });
      console.log('Complete usage data:', completeUsageData);
      
      // Store the complete usage data for tooltips
      setUsageData(completeUsageData);
      
      const colors = getChartColors();
      
      const chartDataObject = {
        labels,
        datasets: [
          {
            label: 'Successful Lookups',
            data: successfulData,
            borderColor: colors.successful.border,
            backgroundColor: colors.successful.background,
            tension: 0.1,
          },
          {
            label: 'Failed Lookups',
            data: failedData,
            borderColor: colors.failed.border,
            backgroundColor: colors.failed.background,
            tension: 0.1,
          },
        ],
      };
      
      console.log('Final chart data object:', chartDataObject);
      
      setChartData(chartDataObject);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
      setError(error.response?.data?.error || 'Failed to fetch usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchUsageData();
    }
  }, [dateRange, user]);

  // Update chart colors when theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (chartData) {
            const colors = getChartColors();
            setChartData(prevData => ({
              ...prevData,
              datasets: prevData.datasets.map((dataset, index) => ({
                ...dataset,
                borderColor: index === 0 ? colors.successful.border : colors.failed.border,
                backgroundColor: index === 0 ? colors.successful.background : colors.failed.background,
              }))
            }));
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, [chartData]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getChartOptions = () => {
    const isDarkMode = document.documentElement.classList.contains('dark-mode');
    const textColor = isDarkMode ? 'rgb(247, 250, 252)' : 'rgb(31, 41, 55)';
    const gridColor = isDarkMode ? 'rgba(74, 85, 104, 0.5)' : 'rgba(229, 231, 235, 0.5)';
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: textColor,
            usePointStyle: true,
          },
        },
        title: {
          display: true,
          text: 'Daily Usage',
          color: textColor,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        tooltip: {
          callbacks: {
            afterBody: function(context) {
              const dataIndex = context[0].dataIndex;
              const dayData = usageData[dataIndex];
              
              // Only show failure breakdown for failed lookups (dataset index 1)
              if (context[0].datasetIndex === 1 && dayData && dayData.failed_lookups > 0) {
                const failureReasons = dayData.failure_reasons;
                if (Object.keys(failureReasons).length > 0) {
                  const breakdown = ['', 'Failure breakdown:'];
                  Object.entries(failureReasons).forEach(([reason, count]) => {
                    // Format reason names to be more readable
                    const formattedReason = reason
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase());
                    breakdown.push(`• ${formattedReason}: ${count}`);
                  });
                  return breakdown;
                }
              }
              return [];
            }
          }
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: {
              size: 12,
            },
          },
          grid: {
            color: gridColor,
          },
          title: {
            display: true,
            text: 'Date',
            color: textColor,
            font: {
              size: 14,
              weight: 'bold',
            },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            color: textColor,
            font: {
              size: 12,
            },
          },
          grid: {
            color: gridColor,
          },
          title: {
            display: true,
            text: 'Number of Lookups',
            color: textColor,
            font: {
              size: 14,
              weight: 'bold',
            },
          },
        },
      },
    };
  };

  return (
    <div className="usage-container">
      <div className="usage-header">
        <h3>Usage</h3>
        <div className="date-range-controls">
          <div className="date-input-group">
            <label htmlFor="startDate">From:</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              min="2025-06-05"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className="date-input-group">
            <label htmlFor="endDate">To:</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              min="2025-06-05"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>
      
      {error && (
        <div className="usage-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      )}
      
      <div className="usage-chart-container">
        {loading ? (
          <div className="usage-loading">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            <span>Loading usage data...</span>
          </div>
        ) : chartData ? (
          <div className="chart-wrapper">
            <Line data={chartData} options={getChartOptions()} />
          </div>
        ) : (
          <div className="usage-empty">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/>
              <path d="M18 17l-3-3-3 3-3-3-3 3"/>
            </svg>
            <p>No usage data available for the selected date range.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Usage; 